import '@babel/polyfill';
import 'prismjs';

import Vue from 'vue';
import axios from 'axios';
import PortalVue from 'portal-vue';
import VueTippy from 'vue-tippy';
import qs from 'qs';
import { pickBy } from 'lodash';
import filters from 'common/filters';
import { Notifier } from 'common/plugins/Notifier';
import { LanguageService } from 'common/services/LanguageService';
import { VueApplication } from 'common/framework/applications/VueApplication';
import Tooltip from 'ui/v1/tooltip/Tooltip';

// Global Error component
import ErrorRefresh from 'components/ui/ErrorRefresh.vue';

import ENV from '../config/env';
import { Authenticator } from 'packages/authentication/services/Authenticator';
import { container } from 'tsyringe';
import { datadogLogs } from '@datadog/browser-logs';
import { Stonehenge } from '@jobilla/stonehenge-js-sdk';
import { FeatureToggleService } from '@jobilla/feature-toggles';
import posthog from 'posthog-js';

import i18n from './i18n';
import * as Sentry from '@sentry/vue';

function shouldReport(error) {
    // We exclude network errors and request failed errors from sentry reports but still handle them
    // e.g. log to console, etc.
    const requestFailedErr = error.message?.includes('Request failed');
    const networkErr = error.message?.includes('Network Error');
    const canceledErr = axios.isCancel(error) || error === 'canceled';
    if (canceledErr || requestFailedErr || networkErr) {
        return false;
    }

    return true;
}

if (['staging', 'production'].includes(ENV.APP_ENV)) {
    datadogLogs.init({
        clientToken: ENV.DATADOG_TOKEN,
        site: 'datadoghq.eu',
        forwardErrorsToLogs: true,
        sampleRate: 100,
        service: 'Jobilla SPAs',
        env: ENV.APP_ENV,
    });
}
// Add Moment to global for access by all components
global.moment = require('moment');

// Application singleton
/** @type {JobillaSpaApplication} */
let _instance = null;
export default class JobillaSpaApplication extends VueApplication {
    constructor(requiresAuth) {
        super();

        if (_instance) {
            if (requiresAuth !== undefined && requiresAuth !== null) {
                _instance.requiresAuth = requiresAuth;
            }
            return _instance;
        }
        _instance = this;

        this.appName = 'Jobilla Web Application';

        /**
         * Total of uncaught exceptions
         */
        this.errorCount = 0;

        this.notifier = new Notifier();

        // Register core plugins
        this.registerPlugins([this.notifier, PortalVue, VueTippy]);

        // Register the global error component
        this.registerComponents({
            ErrorRefresh,
        });

        this.registerDirectives([
            Tooltip,
        ]);

        for (const name in filters) {
            Vue.filter(name, filters[name]);
        }

        this.authenticator = container.resolve(Authenticator);

        // Init services
        this.i18n = i18n;

        this.languageService = new LanguageService(this.i18n);

        // Init the notifier
        this.notifier.i18n = this.i18n;

        // Does the app require auth
        this.requiresAuth = requiresAuth;

        /** @var {import('vuex').Store} */
        this.vuexStore = null;
        this.dataDogLogger = datadogLogs.logger;
        this.stonehengeLogger = Stonehenge.instance;
        this.ft = container.resolve(FeatureToggleService);
    }

    /**
     * Fetches current Company from URL params or LocalStorage
     */
    get currentCompanyId() {
        const companyIdFromRoute = parseInt(this.router?.currentRoute?.params?.companyId);

        if (Number.isFinite(companyIdFromRoute)) {
            return companyIdFromRoute;
        }

        return this.authenticator.getSelectedCompany();
    }

    /**
     * Authenticate the user
     *
     * Use the values stored in the Authenticator (LocalStorage) to validate the user or
     * redirect to the login url.
     */
    auth() {
        if (!this.requiresAuth) {
            return Promise.resolve(true);
        }

        return this.authenticator.check()
            .then(() => {
                if (this.router.currentRoute.query?.['access-token']) {
                    // Clear the token from URL params after a successful auth check
                    this.router.push({
                        query: pickBy(this.router.currentRoute.query, (value, attribute) => attribute !== 'access-token'),
                    })
                        .catch(() => null);
                }
            });
    }

    updateAppVarsFromUrl() {
        const urlParameters = qs.parse(window.location.search.slice(1));
        const token = urlParameters['access-token'] || null;
        const companyId = urlParameters.company_id || null;

        if (token) {
            this.authenticator.updateAuthVariables(token, companyId);
        }

        const accessToken = this.authenticator.getToken();

        if (typeof accessToken === 'string' && accessToken.length > 0) {
            // Set the bearer to the token
            axios.defaults.headers.common.Authorization = `Bearer ${this.authenticator.getToken()}`;
        }
    }

    withStore(store) {
        this.vuexStore = store;

        return this;
    }

    start() {
        this.updateAppVarsFromUrl();

        const config = { i18n: this.i18n };

        if (this.vuexStore) {
            config.store = this.vuexStore;
        }

        const handlePermissions = () => {
            // If there are no indications of an authenticated user, just skip this step, because
            // the API will respond with a 401 without a token.
            if (!this.authenticator.getToken()) {
                return;
            }

            return this.authenticator.fetchPermissions()
                .catch((e) => {
                    if (e.response?.status === 401) {
                        throw e;
                    }

                    // Ignoring any errors but a 401 response. They should be reported to Sentry,
                    // but they should not cause the app to completely fail, or cause
                    // the user to be logged out.
                    this.reportToSentry(e);
                });
        };

        this.auth()
            .then(handlePermissions)
            .then(async () => {
                // If user is not authenticated abort this step.
                if (!this.authenticator.currentUserId) {
                    return;
                }

                // Only load units if the user has any roles in its JWT, because roles means they
                // are a Jobillan, and only then we would need the unit list. Otherwise it would
                // be a waste of bandwidth for thousands of non-Jobillan users.
                if (Object.keys(this.authenticator.permissionData.unitRoles).length) {
                    await this.vuexStore.dispatch('units/getUnits');
                }

                this.vuexStore.watch(
                    state => [state.profile.user, state.companyModule.company, state.units.units],
                    /**
                     * @param {import('packages/users/User').User} user
                     * @param {import('packages/company/entities/Company').Company} company
                     * @param {import('packages/units/Unit').Unit[]} units
                     */
                    ([user, company, units]) => {
                        if (!user?.id || !company?.id) {
                            return;
                        }
                        posthog.identify(user.id.toString(), {
                            jobillaRole: Object.values(this.authenticator.permissionData.unitRoles)?.[0]?.[0],
                            hasJobillaEmail: user.email.includes('@jobilla.com'),
                            jobillaUnit: units ? units.find(u => u.id === Object.keys(this.authenticator.permissionData.unitRoles)[0])?.name : null,
                            language: user.language,
                        });

                        /**
                         * @see https://linear.app/jobilla/issue/ENG-1273/candidate-export-posthog-events-from-companies-that-dont-use-ats
                         */
                        posthog.register({
                            companyId: company.id,
                            companyName: company.name,
                            jobillaRole: Object.values(this.authenticator.permissionData.unitRoles)?.[0]?.[0],
                            jobillaUnit: units ? units.find(u => u.id === Object.keys(this.authenticator.permissionData.unitRoles)[0])?.name : null,
                        });
                    },
                );
            })
            .then(() => {
                this.startApp(config);
            })
            .catch(() => {
                if (this.authenticator.getToken()) {
                    this.authenticator.logOut();
                }

                this.redirectToLogin();
            });

        // Add the vue handler and a global window version
        Vue.config.errorHandler = err => this.handleError(err);
        window.onerror = err => this.handleError(err);
        window.addEventListener('unhandledrejection', (event) => {
            this.handleError(event.reason);
        });

        // Handler to warn not error log vue warnings
        Vue.config.warnHandler = (err, vm, info) => {
            // Suppress warnings about ApplicationView scopedSlots on slots
            if (vm && vm.constructor.options.name === 'ApplicationView'
                && err.toLowerCase()
                    .includes('duplicate presence of slot')) {
                return;
            }

            // It's okay to log to console in environments that we don't report to sentry in.
            if (!this.shouldReportToSentry()) {
                // eslint-disable-next-line no-console
                console.warn(`${err} ${info}`);
            }
        };
    }

    redirectToHome() {
        window.location.replace(ENV.JOBILLA_URL);
    }

    redirectToLogin() {
        this.authenticator.redirectToLogin();
    }

    /**
     * Notify user of error and refresh.
     *
     * All errors are logged through sentry but the modal is only shown
     * for the first error that has occurred.
     *
     * @param {any} err
     * @returns
     *
     * @memberOf JobillaSpaApplication
     */
    handleError(err) {
        const senselessErr = !err;
        const resizeObserverLoopErr = (err === 'ResizeObserver loop limit exceeded' || err === 'ResizeObserver loop completed with undelivered notifications.');
        const JdsCancelErr = (err?.name === 'JdsError' && err?.type === 'cancel');

        if (senselessErr || resizeObserverLoopErr || JdsCancelErr || !shouldReport(err)) {
            return;
        }

        if (this.shouldReportBugToConsole()) {
            /* eslint-disable no-console */
            console.error(err);
        }

        if (!this.shouldReportToSentry()) {
            return this.notifier.toast('Caught JS Error - see console for details', { type: 'error' });
        }

        this.reportToSentry(err, null, { uncaughtError: true });

        this.errorCount++;

        // Only show toast for the first error
        if (this.errorCount > 1) {
            return;
        }

        if (typeof window.hj === 'function') {
            const firstUnit = Object.values(this.authenticator.permissionData.unitRoles)[0];
            window.hj('identify', this.authenticator.currentUserId, {
                role_name: firstUnit?.[0],
            });
            window.hj('event', 'general.error');
        }

        return this.notifier.toast(
            'Oh no, something went wrong! An error report has been sent to engineering automatically.',
            { type: 'error' });
    }

    shouldReportToSentry() {
        return ['production', 'staging'].includes(ENV.APP_ENV);
    }

    shouldReportBugToConsole() {
        return !['production'].includes(ENV.APP_ENV);
    }

    reportToSentry(error) {
        if (!this.shouldReportToSentry()) {
            return;
        }
        Sentry.setContext('Company', { companyId: this.currentCompanyId });
        Sentry.setUser({ id: this.authenticator.currentUserId });
        // Add options to report to sentry
        Sentry.captureException(error);
    }

    configureRoutes(routes, config) {
        super.configureRoutes(routes, config);
    }
}
